import React, { useState, useEffect, useRef, useReducer } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import {
  FLOOR_MATERIALS, WALL_MATERIALS, CEILING_MATERIALS,
  backToPlan, setDesignTab, applyMaterial, overrideStyleColor, buildStyleColorOverride, setCustomMaterial, resetRoomDesign, setDesignLayer, setWallPatches, restoreWallPatches, setFloorPatches, restoreFloorPatches,
} from '../../store/houseSlice';
import { useT, Tx } from '../../i18n';
import { useAuth } from '../../context/AuthContext';
import { saveProjectToWeb } from '../../firebase/workspaceService';
import { store } from '../../store';
import { useUnsavedChanges } from '../../context/UnsavedChangesContext';
import { useAutoSave } from '../../hooks/useAutoSave';
import { useGuestMode } from '../../context/GuestModeContext';
import GuestSaveButton from '../GuestSaveButton';
const FILE_VERSION = 1;
// Guest ("/try") users get a taste of the material catalog before signing up —
// the first N materials per active category/filter stay clickable, the rest
// render locked with a "Sign up for more" overlay (see MaterialCard below).
const GUEST_UNLOCKED_MATERIALS = 6;

import ThreeDRoom from './ThreeDRoom';
import './RoomEditor.css';

const TABS = [
  { id: 'floor',   label: 'Floor',   icon: '⬛' },
  { id: 'wall',    label: 'Walls',   icon: '🟫' },
  { id: 'ceiling', label: 'Ceiling', icon: '⬜' },
];

const CATEGORIES = {
  floor:   ['wood', 'laminate', 'stone', 'tile', 'carpet'],
  wall:    ['neutral', 'colour', 'texture', 'pattern'],
  ceiling: ['plain', 'texture'],
};

const CAT_LABELS = {
  wood: 'Wood', laminate: 'Laminate', stone: 'Stone', tile: 'Tile', carpet: 'Carpet', vinyl: 'Vinyl',
  neutral: 'Neutral', colour: 'Color', texture: 'Textured', pattern: 'Pattern',
  plain: 'Plain', texture_c: 'Textured',
};

const DEFAULT_CUSTOM_COLORS = { floor: '#c8956c', wall: '#e8e0d0', ceiling: '#f5f5f0' };

function MaterialCard({ mat, isSelected, onSelect, layer, locked, lockedLabel }) {
  const swatchStyle = layer === 'floor'
    ? { background: mat.baseColor || mat.color }
    : { background: mat.color };

  return (
    <button
      className={`mat-card ${isSelected ? 'selected' : ''}${locked ? ' mat-card--locked' : ''}`}
      onClick={locked ? undefined : onSelect}
      disabled={locked}
    >
      <div className="mat-swatch" style={swatchStyle} />
      <span className="mat-label">{mat.label}</span>
      {isSelected && <div className="mat-check">✓</div>}
      {locked && (
        <div className="mat-card-lock">
          <svg width="15" height="15" viewBox="0 0 14 14" fill="none">
            <rect x="3" y="6.5" width="8" height="6" rx="1" stroke="currentColor" strokeWidth="1.2"/>
            <path d="M4.5 6.5V4.5a2.5 2.5 0 015 0v2" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
          </svg>
          <span>{lockedLabel}</span>
        </div>
      )}
    </button>
  );
}

export default function RoomEditor({ sharedMode = false }) {
  const dispatch = useDispatch();
  const { selectedRoomId, rooms, designs, designTab } = useSelector(s => s.house);
  const { t } = useT();
  const { user } = useAuth();
  const { projectId } = useParams();
  const isGuest = useGuestMode();
  const { markSaved, isDirty, changeToken, confirmIfDirty } = useUnsavedChanges();

  const [webSaving, setWebSaving] = useState(false);
  // While the user is actively editing inside the 3D view, autosave is
  // paused — ThreeDRoom saves explicitly (once) when "Done" is clicked
  // instead, so a whole editing session costs one save request instead of
  // one per ~1.5s debounce window. See useAutoSave's `enabled` below.
  const [in3DEditMode, setIn3DEditMode] = useState(false);

  async function handleWebSave() {
    if (!projectId || !user) return;
    setWebSaving(true);
    // read directly from store so we always get the state AFTER the latest dispatch
    const s = store.getState().house;
    const sceneData = JSON.stringify({
      version: FILE_VERSION, savedAt: new Date().toISOString(),
      phase: s.phase, rooms: s.rooms, designs: s.designs, furniture: s.furniture, lighting: s.lighting || [],
      wallSegments: s.wallSegments || [], autoWallOpenings: s.autoWallOpenings || {},
      wallHeight: s.wallHeight ?? 2.8, nextId: s.nextId,
      nextFurnitureId: s.nextFurnitureId, nextLightingId: s.nextLightingId || 1, nextOpeningId: s.nextOpeningId,
      nextWallId: s.nextWallId || 1, nextWallOpeningId: s.nextWallOpeningId || 1,
    });
    try {
      await saveProjectToWeb(projectId, sceneData);
      markSaved();
    } catch (err) {
      console.error('Web save failed:', err);
    } finally {
      setWebSaving(false);
    }
  }

  // Autosave to the cloud shortly after the user stops editing (material
  // changes, and furniture/opening edits made inside the 3D room view).
  useAutoSave({
    isDirty, changeToken, save: handleWebSave,
    enabled: !!projectId && !!user && !in3DEditMode,
  });
  const [activeCat, setActiveCat] = useState({});
  const [customColors, setCustomColors]   = useState(DEFAULT_CUSTOM_COLORS);
  const [customLabel,  setCustomLabel]    = useState({ floor: '', wall: '', ceiling: '' });
  // Live preview: holds a custom mat object per layer while user is tweaking (not yet applied)
  const [livePreview,  setLivePreview]    = useState({ floor: null, wall: null, ceiling: null });

  // Per-layer "Back" (undo): a stack of previous values for each layer. The
  // current value is pushed just before a change, so Back restores the last one.
  // Cleared whenever the selected room changes (history is per-room).
  const layerHist = useRef({ floor: [], wall: [], ceiling: [] });
  const [, forceHistUpdate] = useReducer((x) => x + 1, 0);
  useEffect(() => { layerHist.current = { floor: [], wall: [], ceiling: [] }; forceHistUpdate(); }, [selectedRoomId]);

  // Accent-area (per-cell) override for floor + wall: arm click-to-pick in the
  // 3D preview, then paint ~1m grid cells with their own colour/texture.
  // `areaSelect` = picking is armed; `selectedSurface` = which surface the
  // current selection is on ('floor', a wall faceId); `selectedCells` = the
  // picked cell keys ("col-row").
  const [areaSelect, setAreaSelect]             = useState(false);
  const [selectedSurface, setSelectedSurface]   = useState(null);
  const [selectedCells, setSelectedCells]       = useState([]);
  // Material staged for the selected cells — a preset id or a custom material
  // object. Chosen from the list/colour picker but only committed on Apply.
  const [pendingCellMat, setPendingCellMat]     = useState(null);
  // Selection is per-surface — reset when the room changes or the tab changes.
  const resetAreaSel = () => { setAreaSelect(false); setSelectedSurface(null); setSelectedCells([]); setPendingCellMat(null); };
  useEffect(() => { resetAreaSel(); }, [selectedRoomId]);
  useEffect(() => { resetAreaSel(); }, [designTab]);

  const room = rooms.find(r => r.id === selectedRoomId);
  const design = designs[selectedRoomId] || {};

  if (!room) return null;

  const materials = {
    floor: FLOOR_MATERIALS,
    wall: WALL_MATERIALS,
    ceiling: CEILING_MATERIALS,
  };

  const currentSelected = design[designTab];
  // Floor and wall support per-area (per-cell) painting; ceiling doesn't.
  const areaCapable = designTab === 'floor' || designTab === 'wall';

  // When painting a selected AREA, colour edits must recolour THAT area's own
  // material (keeping its texture/pattern), not the whole layer's. The area's
  // effective material is the one STAGED for it (pendingCellMat — a style just
  // clicked, or an in-progress recolour), falling back to what's already saved
  // on the cells. From it we derive the base style id to recolour.
  const areaMap = designTab === 'floor' ? (design.floorPatches || {}) : (design.wallPatches?.[selectedSurface] || {});
  const areaSavedMat = (selectedSurface && selectedCells.length &&
    selectedCells.every(k => (areaMap[k] ?? null) === (areaMap[selectedCells[0]] ?? null)))
    ? (areaMap[selectedCells[0]] ?? null) : null;
  const areaStagedMat = pendingCellMat != null ? pendingCellMat : areaSavedMat;
  const areaBaseStyleId = typeof areaStagedMat === 'string' ? areaStagedMat
    : (areaStagedMat && typeof areaStagedMat === 'object' && areaStagedMat.__colorOverride ? areaStagedMat.id : null);

  // Build the material for the selected area at a given colour — recolour its
  // own base style (preserving the texture) when it has one, else a flat fill.
  const buildAreaMat = (color, label) => {
    if (areaBaseStyleId) {
      const ov = buildStyleColorOverride(designTab, areaBaseStyleId, color, null, label);
      if (ov) return ov;
    }
    return { __custom: true, id: `__custom_${designTab}`, label: label || 'Custom', color, baseColor: color, category: 'custom' };
  };

  // Snapshot the current value of a layer before it changes (call right before
  // any dispatch that changes it). For floor/wall this captures BOTH the whole
  // material and the per-area accent patches, so Back undoes either kind of
  // change in order.
  const recordUndo = (layer) => {
    if (layer === 'wall') {
      layerHist.current.wall.push({
        wall: design.wall ?? null,
        wallPatches: design.wallPatches ? JSON.parse(JSON.stringify(design.wallPatches)) : null,
      });
    } else if (layer === 'floor') {
      layerHist.current.floor.push({
        floor: design.floor ?? null,
        floorPatches: design.floorPatches ? JSON.parse(JSON.stringify(design.floorPatches)) : null,
      });
    } else {
      layerHist.current[layer].push({ value: design[layer] ?? null });
    }
    forceHistUpdate();
  };
  const canUndoLayer = layerHist.current[designTab].length > 0;
  // Revert the active layer to its previous snapshot.
  const handleLayerBack = () => {
    const stack = layerHist.current[designTab];
    if (!stack.length) return;
    const prev = stack.pop();
    forceHistUpdate();
    if (designTab === 'wall') {
      dispatch(setDesignLayer({ roomId: selectedRoomId, layer: 'wall', value: prev.wall }));
      dispatch(restoreWallPatches({ roomId: selectedRoomId, wallPatches: prev.wallPatches }));
      resetAreaSel();
    } else if (designTab === 'floor') {
      dispatch(setDesignLayer({ roomId: selectedRoomId, layer: 'floor', value: prev.floor }));
      dispatch(restoreFloorPatches({ roomId: selectedRoomId, floorPatches: prev.floorPatches }));
      resetAreaSel();
    } else {
      dispatch(setDesignLayer({ roomId: selectedRoomId, layer: designTab, value: prev.value }));
    }
    setLivePreview(p => ({ ...p, [designTab]: null }));
    handleWebSave();
  };

  // A default style is "active" for this layer when the stored value is either
  // the bare preset id, or that same preset already recoloured by hand — in
  // both cases the manual panel below edits its colour instead of starting a
  // brand-new flat custom material, so the underlying pattern/texture is kept.
  const baseStyleId = typeof currentSelected === 'string'
    ? currentSelected
    : (currentSelected && typeof currentSelected === 'object' && currentSelected.__colorOverride ? currentSelected.id : null);

  // Sync the manual colour picker to whatever is currently applied for this
  // layer — a custom flat material, a recoloured preset, or an untouched
  // preset — so "manually change the color" always starts from what's shown.
  React.useEffect(() => {
    if (currentSelected && typeof currentSelected === 'object' && currentSelected.__custom) {
      setCustomColors(p => ({ ...p, [designTab]: currentSelected.color || p[designTab] }));
      setCustomLabel(p => ({ ...p, [designTab]: currentSelected.label !== 'Custom' ? currentSelected.label : p[designTab] }));
    } else if (currentSelected && typeof currentSelected === 'object' && currentSelected.__colorOverride) {
      setCustomColors(p => ({ ...p, [designTab]: currentSelected.baseColor || currentSelected.color || p[designTab] }));
      setCustomLabel(p => ({ ...p, [designTab]: currentSelected.label || p[designTab] }));
    } else if (typeof currentSelected === 'string') {
      const baseMat = materials[designTab].find(m => m.id === currentSelected);
      if (baseMat) {
        setCustomColors(p => ({ ...p, [designTab]: baseMat.baseColor || baseMat.color || p[designTab] }));
        setCustomLabel(p => ({ ...p, [designTab]: baseMat.label || p[designTab] }));
      }
    }
  }, [designTab, selectedRoomId, currentSelected]); // eslint-disable-line

  // When a painted area is selected, start the colour picker from THAT area's
  // colour (not the whole layer's), so a colour tweak edits what's actually
  // shown on the selected cells. Only fires when the area's material changes
  // (new selection / after Apply), never mid-edit — so it won't fight typing.
  const areaCurrentColor = (() => {
    const v = areaStagedMat;
    if (!v) return null;
    if (typeof v === 'string') { const m = materials[designTab].find(mm => mm.id === v); return m?.baseColor || m?.color || null; }
    return v.baseColor || v.color || null;
  })();
  React.useEffect(() => {
    if (areaCurrentColor) setCustomColors(p => ({ ...p, [designTab]: areaCurrentColor }));
  }, [areaCurrentColor, designTab, selectedSurface]); // eslint-disable-line

  const cats = CATEGORIES[designTab] || [];
  const selectedCat = activeCat[designTab] || null;
  const allMats = materials[designTab];
  const currentMats = selectedCat
    ? allMats.filter(m => m.category === selectedCat)
    : allMats;


  // Design with live custom preview overlaid for the current layer
  const previewDesign = {
    ...design,
    ...(livePreview[designTab] ? { [designTab]: livePreview[designTab] } : {}),
  };

  // Build a live-preview mat from the current custom picker values — keeps the
  // active preset's pattern/texture (recoloured) when one is selected,
  // otherwise a flat custom material.
  const buildLiveMat = (color, label) => {
    if (baseStyleId) {
      const override = buildStyleColorOverride(designTab, baseStyleId, color, null, label);
      if (override) return override;
    }
    return {
      __custom: true,
      id: `__custom_${designTab}`,
      label: label || 'Custom',
      color,
      baseColor: color,
      category: 'custom',
    };
  };

  const handleCustomColorChange = (color) => {
    setCustomColors(p => ({ ...p, [designTab]: color }));
    // Editing area cells → stage a recolour of the AREA's own material (keeping
    // its texture), previewed on the selected cells and committed on Apply.
    if (areaCapable && selectedSurface) {
      setPendingCellMat(buildAreaMat(color, customLabel[designTab]));
      return;
    }
    setLivePreview(p => ({ ...p, [designTab]: buildLiveMat(color, customLabel[designTab]) }));
  };

  // ── Accent area (per-cell override for floor + wall) ─────────────
  // True while one or more ~1m cells are selected for their own material.
  const areaEditActive = areaCapable && !!selectedSurface && selectedCells.length > 0;
  // The material shared by every selected cell, if uniform (for card highlight).
  const faceCurrentVal = (() => {
    if (!areaEditActive) return null;
    const map = designTab === 'floor' ? (design.floorPatches || {}) : (design.wallPatches?.[selectedSurface] || {});
    const first = map[selectedCells[0]] ?? null;
    return selectedCells.every(k => (map[k] ?? null) === first) ? first : null;
  })();
  const FACE_LABELS = { floor: 'Floor', back: 'Back wall', front: 'Front wall', left: 'Left wall', right: 'Right wall' };
  const faceLabel = (id) => FACE_LABELS[id]
    || (typeof id === 'string' && id.startsWith('iw-') ? 'Interior wall' : null)
    || (id ? `Wall ${String(id).replace(/^seg-?/, '')}` : '');
  // A cell was clicked in the preview: (re)start selection on a new surface,
  // else toggle the cell in the current surface's selection.
  const handleAreaSelect = (surfaceId, cellKey) => {
    if (surfaceId !== selectedSurface) { setSelectedSurface(surfaceId); setSelectedCells([cellKey]); return; }
    const next = selectedCells.includes(cellKey) ? selectedCells.filter(k => k !== cellKey) : [...selectedCells, cellKey];
    setSelectedCells(next);
    if (next.length === 0) setSelectedSurface(null); // no cells left → drop focus
  };
  // Persist a material `value` (preset id, custom/override object, or null to
  // clear) onto every selected cell of the current surface and save.
  const applyAreaCells = (value) => {
    if (!selectedSurface || !selectedCells.length) return;
    recordUndo(designTab); // snapshot layer + patches so Back can revert this paint/erase
    if (designTab === 'floor') dispatch(setFloorPatches({ roomId: selectedRoomId, cells: selectedCells, value }));
    else dispatch(setWallPatches({ roomId: selectedRoomId, faceId: selectedSurface, cells: selectedCells, value }));
    setLivePreview(p => ({ ...p, [designTab]: null }));
    handleWebSave();
    // A real material was applied → finish: exit select mode and clear the
    // selection. (Erase, value == null, keeps the selection to tweak further.)
    if (value != null) resetAreaSel();
  };

  const handleApplyCustom = () => {
    // Editing selected area cells → commit the staged material (a picked preset
    // or custom colour) to those cells, not the whole layer.
    if (areaEditActive) {
      const value = pendingCellMat != null
        ? pendingCellMat
        : buildAreaMat(customColors[designTab], customLabel[designTab]);
      applyAreaCells(value);
      return;
    }
    recordUndo(designTab);
    if (baseStyleId) {
      dispatch(overrideStyleColor({
        roomId: selectedRoomId,
        layer: designTab,
        styleId: baseStyleId,
        color: customColors[designTab],
        label: customLabel[designTab],
      }));
    } else {
      dispatch(setCustomMaterial({
        roomId: selectedRoomId,
        layer: designTab,
        color: customColors[designTab],
        label: customLabel[designTab] || 'Custom',
      }));
    }
    setLivePreview(p => ({ ...p, [designTab]: null }));
    handleWebSave();
  };

  const currentColorField = currentSelected && typeof currentSelected === 'object'
    ? (currentSelected.baseColor || currentSelected.color)
    : null;

  const isCustomApplied = baseStyleId
    ? (typeof currentSelected === 'object' && currentSelected?.__colorOverride
        && currentSelected.id === baseStyleId && currentColorField === customColors[designTab])
    : (typeof currentSelected === 'object' && currentSelected?.__custom
        && currentColorField === customColors[designTab]);

  return (
    // ── EDITING THE SCENE TAKES THE SCREEN, ON A PHONE ───────────────────────
    // `in3DEditMode` already existed here to pause autosave; it is also the
    // only thing outside ThreeDRoom that knows the 3D view has panels open.
    // Every one of them — the item settings sheet and all four add-pickers —
    // is gated on the same `editMode` inside that component, so this class is
    // an exact statement of "a panel may be on screen right now".
    //
    // What it is for: on a phone the material picker takes 42vh, leaving the
    // 3D view about 40vh, and the panels are positioned INSIDE that view. The
    // sheet then covered the whole of it and you could not see the thing you
    // were editing. The CSS collapses the picker for the duration, so the view
    // — and therefore the panels' container — is the whole page.
    <div className={`room-editor-page${in3DEditMode ? ' room-editor-page--editing' : ''}`}>
      {/* Header — hidden in shared-edit mode; the shared viewer has its own top
          bar (and Back/Reset go to the Builder flow, which shared editors lack). */}
      {!sharedMode && (
      <header className="re-header">
        <button className="re-back-btn" onClick={() => confirmIfDirty(() => dispatch(backToPlan()))}>
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M9 2.5L4.5 7L9 11.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
          <Tx k="floor_plan_back" />
        </button>

        <div className="re-title" >
          <div>
            <span className="re-eyebrow">{t('step3')}</span>
            <h2 style={{textAlign:'center'}}>{room.label}</h2>
          </div>
        </div>

        <div className="re-header-actions">
          {/* The "Complete" badge and the "Saved" chip both used to sit here.
              Neither did anything: the badge restated what the tab dots already
              show, and the save state is autosaved. Between them they took most
              of a phone header's width to tell the user nothing actionable.
              The guest save button stays — it is a real action, and a guest has
              no autosave behind it. */}
          {isGuest && <GuestSaveButton />}
          <button className="re-reset-btn" onClick={() => dispatch(resetRoomDesign(selectedRoomId))}>
            <Tx k="reset" />
          </button>
        </div>
      </header>
      )}

      <div className="re-body">
        {/* ISO Preview */}
        <div className="re-preview-col">
          <div className="re-preview-label">
            <span>{t('live_preview')}</span>
            <span className="re-preview-dims">{room.w * 2}m × {room.h * 2}m</span>
          </div>
          <div className="re-iso-wrapper">
            <ThreeDRoom room={room} design={previewDesign} onWebSave={handleWebSave} onEditModeChange={setIn3DEditMode}
              selectTarget={areaSelect ? designTab : null} selectedSurface={selectedSurface} selectedCells={selectedCells} cellPreview={pendingCellMat} onAreaSelect={handleAreaSelect} />
          </div>
        </div>

        {/* Material picker */}
        <div className="re-picker-col">
          <div className="re-tabs">
            {TABS.map(tab => (
              <button
                key={tab.id}
                className={`re-tab ${designTab === tab.id ? 'active' : ''}`}
                onClick={() => dispatch(setDesignTab(tab.id))}
              >
                <span>{tab.icon}</span>
                <Tx k={tab.id === 'floor' ? 'floor_tab' : tab.id === 'wall' ? 'walls_tab' : 'ceiling_tab'} />
                {design[tab.id === 'wall' ? 'wall' : tab.id] && (
                  <span className="re-tab-dot"/>
                )}
              </button>
            ))}
          </div>

          {/* Per-layer Back (undo) — reverts the ACTIVE layer to its previous
              material. Disabled until this layer has a change to undo. */}
          <button
            className="re-layer-back"
            onClick={handleLayerBack}
            disabled={!canUndoLayer}
            title={t('layer_undo_hint')}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              margin: '8px 0 0', padding: '6px 10px', width: '100%',
              borderRadius: 8, cursor: canUndoLayer ? 'pointer' : 'default',
              fontSize: 12, fontWeight: 600, transition: 'all 0.15s',
              // Clear enabled vs disabled contrast: enabled = bright amber accent,
              // disabled = flat, dim and greyed out.
              border: canUndoLayer ? '1px solid rgba(255,200,80,0.55)' : '1px solid rgba(255,255,255,0.07)',
              background: canUndoLayer ? 'rgba(255,200,80,0.15)' : 'rgba(255,255,255,0.02)',
              color: canUndoLayer ? '#ffcc60' : '#565656',
            }}
          >
            <svg width="13" height="13" viewBox="0 0 14 14" fill="none"><path d="M9 2.5L4.5 7L9 11.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/></svg>
            {t('layer_undo')}
          </button>

          <div className="re-picker-scroll">
            {/* ── CUSTOM MATERIAL SECTION (top) ───────────────────── */}
            <div className="re-custom-section">
              <div className="re-custom-header">
                <span className="re-custom-title">{t('custom_material')}</span>
                <span className="re-custom-hint">{t('custom_preview_hint')}</span>
              </div>

              {baseStyleId ? (
                <div className="re-custom-style-badge">
                  {t('editing_color_for')} <strong>{customLabel[designTab]}</strong>
                </div>
              ) : (
                <div className="re-custom-style-hint">{t('color_from_scratch_hint')}</div>
              )}

              <div className="re-custom-color-row">
                <label className="re-custom-label">{t('color_label')}</label>
                <div className="re-custom-color-wrap">
                  <div
                    className="re-custom-swatch"
                    style={{ background: customColors[designTab] }}
                  >
                    <input
                      type="color"
                      className="re-custom-color-input"
                      value={customColors[designTab]}
                      onChange={e => handleCustomColorChange(e.target.value)}
                    />
                  </div>
                  <input
                    type="text"
                    className="re-custom-hex"
                    value={customColors[designTab]}
                    maxLength={7}
                    onChange={e => {
                      const v = e.target.value;
                      if (/^#[0-9a-fA-F]{0,6}$/.test(v)) {
                        setCustomColors(p => ({ ...p, [designTab]: v }));
                        if (v.length === 7) handleCustomColorChange(v);
                      }
                    }}
                  />
                </div>
              </div>

              <input
                type="text"
                className="re-custom-name"
                placeholder="Name (optional)"
                value={customLabel[designTab] || ''}
                onChange={e => setCustomLabel(p => ({ ...p, [designTab]: e.target.value }))}
              />

              <button
                className={`re-custom-apply ${isCustomApplied ? 'applied' : ''}`}
                onClick={handleApplyCustom}
              >
                <Tx k={isCustomApplied ? 'applied' : 'apply'} />
              </button>

              {/* Accent area — arm select mode, then click ~1m cells of one
                  wall or the floor in the 3D preview. The material grid + Apply
                  above target the selected cells until they're deselected. */}
              {areaCapable && (
                <div style={{ marginTop: 10 }}>
                  <button
                    onClick={() => (areaSelect ? resetAreaSel() : setAreaSelect(true))}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                      width: '100%', padding: '8px 10px', borderRadius: 8, fontSize: 12, fontWeight: 600,
                      cursor: 'pointer', transition: 'all 0.15s',
                      // Blue to arm, red while armed (Cancel).
                      border: areaSelect ? '1px solid #fca5a5' : '1px solid #3b82f6',
                      background: areaSelect ? '#ef4444' : '#3b82f6',
                      color: '#ffffff',
                      boxShadow: areaSelect ? '0 0 0 3px rgba(239,68,68,0.30)' : 'none',
                    }}
                  >
                    {areaSelect
                      ? <svg width="13" height="13" viewBox="0 0 14 14" fill="none"><path d="M3.5 3.5l7 7M10.5 3.5l-7 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
                      : <svg width="13" height="13" viewBox="0 0 14 14" fill="none"><path d="M2 5.5L7 2l5 3.5v5.5H2z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round"/></svg>}
                    {areaSelect
                      ? t('cancel')
                      : (designTab === 'floor' ? t('select_floor') : t('select_wall'))}
                  </button>

                  {selectedCells.length > 0 && (
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 6 }}>
                      <span style={{ flex: 1, fontSize: 12, fontWeight: 700, color: '#ffcc60', display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ width: 8, height: 8, borderRadius: 2, background: '#ffcc60', display: 'inline-block' }} />
                        {faceLabel(selectedSurface)} · {selectedCells.length} {t('wall_cells')}
                      </span>
                    </div>
                  )}

                  {areaSelect && selectedCells.length === 0 && (
                    <div style={{ fontSize: 11, color: '#999', marginTop: 6, textAlign: 'center' }}>
                      {designTab === 'floor' ? t('floor_pick_hint') : t('wall_pick_hint')}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="re-divider" />

            <div className="re-section-label">
              {designTab === 'floor' && 'Choose flooring material'}
              {designTab === 'wall' && 'Choose wall color or pattern'}
              {designTab === 'ceiling' && 'Choose ceiling finish'}
            </div>

            {cats.length > 0 && (
              <div className="re-cat-pills">
                <button
                  className={`re-cat-pill ${!selectedCat ? 'active' : ''}`}
                  onClick={() => setActiveCat(p => ({ ...p, [designTab]: null }))}
                >{t('cat_all') || 'All'}</button>
                {cats.map(cat => (
                  <button
                    key={cat}
                    className={`re-cat-pill ${selectedCat === cat ? 'active' : ''}`}
                    onClick={() => setActiveCat(p => ({ ...p, [designTab]: cat }))}
                  >
                    {t('cat_' + cat) || CAT_LABELS[cat] || cat}
                  </button>
                ))}
              </div>
            )}

            <div className="re-mat-grid">
              {currentMats.map((mat, i) => (
                <MaterialCard
                  key={mat.id}
                  mat={mat}
                  layer={designTab}
                  isSelected={areaEditActive
                    ? ((pendingCellMat != null ? pendingCellMat : faceCurrentVal) === mat.id)
                    : (typeof previewDesign[designTab] === 'string' && previewDesign[designTab] === mat.id)}
                  locked={isGuest && i >= GUEST_UNLOCKED_MATERIALS}
                  lockedLabel={t('guest_locked_hint')}
                  onSelect={() => {
                    // Editing selected area cells → just stage the preset; it's
                    // committed to the cells when the user clicks Apply.
                    if (areaEditActive) { setPendingCellMat(mat.id); return; }
                    recordUndo(designTab);
                    dispatch(applyMaterial({ roomId: selectedRoomId, layer: designTab, materialId: mat.id }));
                    setLivePreview(p => ({ ...p, [designTab]: null }));
                    handleWebSave();
                  }}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
